import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatarAteDuasCasas, formatarDataHora, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe, type RetornoDeclarado } from '@/lib/retorno';

import {
  api,
  type CadastrosDaApi,
  type EnvioDePlano,
  type EnvioDeRealizacao,
  type EnvioDeTarefa,
  type ItemDaTarefaDaApi,
  type PlanoDaApi,
  type TarefaDaApi,
  type VencimentoDaApi,
} from './api';
import { UNIDADE_DO_GATILHO } from './vencimento';

export { explicarFalha };

/**
 * O módulo 05 falando em domínio.
 *
 * A invalidação é GROSSA, como na operação: publicar um plano muda a lista de
 * planos E a fila de vencimentos; registrar uma realização muda o vencimento e
 * a lista de realizações. Invalidar só uma deixaria o topo da tela contando uma
 * coisa e a tabela embaixo mostrando outra.
 */
const CHAVE = ['manutencao'] as const;

export function useCadastrosDaManutencao() {
  return useQuery({ queryKey: [...CHAVE, 'cadastros'], queryFn: () => api.cadastros() });
}

/** `habilitada`: so com a licenca da rota (`manutencao.ler`) — sem ela, nem sai (robos, 20/10/2026). */
export function useVencimentos(habilitada = true) {
  return useQuery({
    queryKey: [...CHAVE, 'vencimentos'],
    enabled: habilitada,
    queryFn: async () => (await api.vencimentos()).vencimentos,
  });
}

export function usePlanos() {
  return useQuery({ queryKey: [...CHAVE, 'planos'], queryFn: async () => (await api.planos()).planos });
}

export function usePlano(id: string | null) {
  return useQuery({
    queryKey: [...CHAVE, 'plano', id],
    enabled: id !== null,
    queryFn: () => api.plano(id ?? ''),
  });
}

export function useRealizacoes(equipamentoId: string | null) {
  return useQuery({
    queryKey: [...CHAVE, 'realizacoes', equipamentoId],
    enabled: equipamentoId !== null,
    queryFn: async () => (await api.realizacoes(equipamentoId ?? '')).realizacoes,
  });
}

/** O que a tela já leu da manutenção — o "em quê" do retorno (R04, p.32). */
interface LidoDaManutencao {
  readonly plano: (id: string) => PlanoDaApi | undefined;
  readonly tarefa: (id: string) => { readonly tarefa: TarefaDaApi; readonly plano: PlanoDaApi } | undefined;
  readonly item: (
    id: string,
  ) => { readonly item: ItemDaTarefaDaApi; readonly tarefa: TarefaDaApi; readonly plano: PlanoDaApi } | undefined;
  readonly vencimento: (tarefaId: string, equipamentoId: string) => VencimentoDaApi | undefined;
  readonly cadastros: () => CadastrosDaApi | undefined;
}

const alvoDoPlano = (p: PlanoDaApi | undefined) => (p ? { alvo: { nome: p.nome, ...(p.alvo ? { codigo: p.alvo } : {}) } } : {});
const plural = (n: number, um: string, varios: string) => `${formatarInteiro(n)} ${n === 1 ? um : varios}`;

/**
 * Toda escrita da manutenção declara o seu retorno (R04, p.32). A resposta
 * traz o id; o nome do plano e a tarefa vêm do que a tela já leu.
 */
function useEscrita<A, R>(
  fazer: (argumento: A) => Promise<R>,
  montar: (resposta: R, argumento: A, lido: LidoDaManutencao) => RetornoDeclarado,
) {
  const cliente = useQueryClient();
  const detalhes = () =>
    cliente
      .getQueriesData<{ plano: PlanoDaApi; tarefas: readonly TarefaDaApi[] }>({ queryKey: [...CHAVE, 'plano'] })
      .flatMap(([, d]) => (d ? [d] : []));
  const lido: LidoDaManutencao = {
    plano: (id) =>
      cliente.getQueryData<readonly PlanoDaApi[]>([...CHAVE, 'planos'])?.find((p) => p.id === id) ??
      detalhes().find((d) => d.plano.id === id)?.plano,
    tarefa: (id) => {
      for (const d of detalhes()) {
        const tarefa = d.tarefas.find((t) => t.id === id);
        if (tarefa) return { tarefa, plano: d.plano };
      }
      return undefined;
    },
    item: (id) => {
      for (const d of detalhes()) {
        for (const tarefa of d.tarefas) {
          const item = tarefa.itens.find((i) => i.id === id);
          if (item) return { item, tarefa, plano: d.plano };
        }
      }
      return undefined;
    },
    vencimento: (tarefaId, equipamentoId) =>
      cliente
        .getQueryData<readonly VencimentoDaApi[]>([...CHAVE, 'vencimentos'])
        ?.find((v) => v.tarefaId === tarefaId && v.equipamentoId === equipamentoId),
    cadastros: () => cliente.getQueryData<CadastrosDaApi>([...CHAVE, 'cadastros']),
  };
  return useMutation({
    meta: { retorno: retornoDe<R, A>((r, a) => montar(r, a, lido)) },
    mutationFn: fazer,
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export const useCriarPlano = () =>
  useEscrita(
    (envio: EnvioDePlano) => api.criarPlano(envio),
    (_r, v) => ({
      titulo: 'Plano de manutenção criado',
      alvo: { nome: v.nome },
      fatos: [plural(v.tarefas.length, 'tarefa', 'tarefas')],
      efeito: 'Fica em rascunho até alguém publicar',
    }),
  );
export const useExtrairPlano = () =>
  useEscrita(
    (envio: Parameters<typeof api.extrairPlano>[0]) => api.extrairPlano(envio),
    (r, v, lido) => {
      const documento = lido.cadastros()?.documentos.find((d) => d.id === v.documentoId);
      return {
        titulo: r.criado ? 'Rascunho do plano montado pela IA' : 'A IA não montou o rascunho',
        tom: r.criado && r.descartadas === 0 ? 'sucesso' : 'ressalva',
        alvo: { nome: v.nome ?? documento?.titulo ?? 'Manual escolhido' },
        fatos: [
          r.tarefas === undefined ? null : plural(r.tarefas, 'tarefa extraída', 'tarefas extraídas'),
          r.descartadas > 0 ? plural(r.descartadas, 'descartada na conferência', 'descartadas na conferência') : null,
          r.falta ? `falta: ${r.falta}` : null,
        ],
        ...(r.criado ? { efeito: 'Fica em rascunho até alguém conferir e publicar' } : {}),
      };
    },
  );
export const useAcrescentarTarefa = () =>
  useEscrita(
    ({ planoId, envio }: { planoId: string; envio: EnvioDeTarefa }) => api.acrescentarTarefa(planoId, envio),
    (_r, v, lido) => ({
      titulo: 'Tarefa acrescentada ao plano',
      ...alvoDoPlano(lido.plano(v.planoId)),
      fatos: [v.envio.descricao, `a cada ${formatarAteDuasCasas(v.envio.intervalo)} ${UNIDADE_DO_GATILHO[v.envio.gatilho]}`],
    }),
  );
export const useTirarTarefa = () =>
  useEscrita(
    (tarefaId: string) => api.tirarTarefa(tarefaId),
    (_r, tarefaId, lido) => {
      const achado = lido.tarefa(tarefaId);
      return { titulo: 'Tarefa retirada do plano', ...alvoDoPlano(achado?.plano), fatos: [achado?.tarefa.descricao ?? null] };
    },
  );
export const useAcrescentarItem = () =>
  useEscrita(
    ({ tarefaId, produtoId, quantidade }: { tarefaId: string; produtoId: string; quantidade: number }) =>
      api.acrescentarItem(tarefaId, { produtoId, quantidade }),
    (_r, v, lido) => {
      const achado = lido.tarefa(v.tarefaId);
      const produto = lido.cadastros()?.produtos.find((p) => p.id === v.produtoId);
      return {
        titulo: 'Item acrescentado à lista técnica',
        ...alvoDoPlano(achado?.plano),
        fatos: [
          achado?.tarefa.descricao ?? null,
          produto ? `${produto.codigo} · ${produto.nome}` : null,
          `${formatarAteDuasCasas(v.quantidade)}${produto ? ` ${produto.unidade}` : ''}`,
        ],
      };
    },
  );
export const useTirarItem = () =>
  useEscrita(
    (itemId: string) => api.tirarItem(itemId),
    (_r, itemId, lido) => {
      const achado = lido.item(itemId);
      return {
        titulo: 'Item retirado da lista técnica',
        ...alvoDoPlano(achado?.plano),
        fatos: [achado?.tarefa.descricao ?? null, achado ? achado.item.produto : null],
      };
    },
  );
export const usePublicar = () =>
  useEscrita(
    ({ planoId, nota }: { planoId: string; nota: string }) => api.publicar(planoId, nota),
    (r, v, lido) => ({
      titulo: 'Plano publicado',
      ...alvoDoPlano(lido.plano(v.planoId)),
      fatos: [`nota: ${v.nota}`],
      efeito: r.arquivou ? 'A versão anterior foi arquivada; os vencimentos seguem o plano novo' : 'Os vencimentos passam a seguir este plano',
    }),
  );
export const useRevisar = () =>
  useEscrita(
    (planoId: string) => api.revisar(planoId),
    (_r, planoId, lido) => ({
      titulo: 'Revisão do plano aberta',
      ...alvoDoPlano(lido.plano(planoId)),
      efeito: 'A versão publicada continua valendo até a revisão ser publicada',
    }),
  );
export const useArquivar = () =>
  useEscrita(
    ({ planoId, motivo }: { planoId: string; motivo: string }) => api.arquivar(planoId, motivo),
    (_r, v, lido) => ({ titulo: 'Plano arquivado', ...alvoDoPlano(lido.plano(v.planoId)), fatos: [`motivo: ${v.motivo}`] }),
  );
export const useRegistrarRealizacao = () =>
  useEscrita(
    (envio: EnvioDeRealizacao) => api.registrarRealizacao(envio),
    (_r, v, lido) => {
      const venc = lido.vencimento(v.tarefaId, v.equipamentoId);
      const maquina = lido.cadastros()?.equipamentos.find((e) => e.id === v.equipamentoId);
      return {
        titulo: v.corrigeId ? 'Realização corrigida' : 'Realização registrada',
        ...(maquina ? { alvo: { codigo: maquina.codigo, nome: maquina.descricao } } : venc ? { alvo: { codigo: venc.equipamento } } : {}),
        fatos: [
          venc?.descricao ?? null,
          formatarDataHora(new Date(v.realizadaEm)),
          v.leitura === null || v.leitura === undefined ? null : `leitura ${formatarAteDuasCasas(v.leitura)}`,
        ],
        efeito: 'O próximo vencimento conta a partir desta realização',
      };
    },
  );
